import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';

import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';

import { stripNonNumerals, PHONE_NUMBER_LENGTH_US } from '../phone-number-helper/';

import { STAMPS_SMS_PROMPT, STAMPS_SMS_REQUEST_CODE } from '../analytics-event-strings';

import style from './style.module.less';
import sharedStyle from '../style.module.less';

const SendCodeErrorMessage = () => {
  return (
    <div className={style.error}>
      <FormattedMessage defaultMessage='Something went wrong, please try again.' description='phoneVerify.sendCodeError' id='ZOocGD' />
    </div>
  );
};

export const VerificationPrompt = ({ goToNextStep, requestChallengeCode, trackEvent = noop, className = '', closeModal }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [hasRequestedCode, setHasRequestedCode] = useState(false);
  const [hasSendFailed, setHasSendFailed] = useState(false);

  trackEvent(STAMPS_SMS_PROMPT);

  const onRequestChallengeCode = useCallback(() => {
    const onlyNumerals = stripNonNumerals(phoneNumber);
    setHasSendFailed(false);
    setHasRequestedCode(true);

    trackEvent(STAMPS_SMS_REQUEST_CODE);

    return requestChallengeCode(onlyNumerals)
      .then(({ data = {} }) => {
        const { publicKey } = data;
        goToNextStep({
          phoneNumber: onlyNumerals,
          publicKey,
        });
      })
      .catch(() => {
        setHasSendFailed(true);
        setHasRequestedCode(false);
      });
  }, [goToNextStep, phoneNumber, requestChallengeCode, setHasRequestedCode, setHasSendFailed, trackEvent]);

  const onPhoneNumberChange = useCallback(
    (e) => {
      setPhoneNumber(e.target.value);
    },
    [setPhoneNumber],
  );

  // TODO: better validation, intl
  const isValidPhoneNumber = useCallback(() => {
    const length = stripNonNumerals(phoneNumber).length;
    return length === PHONE_NUMBER_LENGTH_US;
  }, [phoneNumber]);

  const isNextDisabled = !isValidPhoneNumber(phoneNumber) || hasRequestedCode;
  return (
    <React.Fragment>
      <ModalBody className={classnames(style.wrapper, className)}>
        <div className={sharedStyle['title-text']}>
          <FormattedMessage defaultMessage='Verify Your Account' description='phoneVerifyModal.promptTitle' id='UWtuf4' />
        </div>
        <div className={sharedStyle['subtitle-text']}>
          <FormattedMessage
            defaultMessage='We need to authenticate your Stamps account for you to purchase Stamps Postage.'
            description='phoneVerifyModal.promptSubtitle'
            id='8Ix0BX'
          />
        </div>
        <div className={style['phone-number-field']}>
          <label htmlFor='phone-number-input'>
            <FormattedMessage defaultMessage='ENTER PHONE NUMBER' description='phoneVerifyModal.enterPhoneNumber' id='My8BP3' />
          </label>
          <input id='phone-number-input' onChange={onPhoneNumberChange} placeholder='i.e. (512) 313-9458' value={phoneNumber} />
        </div>
        {hasSendFailed && <SendCodeErrorMessage />}
      </ModalBody>
      <ModalFooter className={sharedStyle.footer}>
        <Button onClick={closeModal} theme='secondary'>
          <FormattedMessage defaultMessage='Cancel' description='phoneVerifyModal.cancelButton' id='ZMMmLL' />
        </Button>
        <Button disabled={isNextDisabled} onClick={onRequestChallengeCode} theme='primary'>
          <FormattedMessage defaultMessage='Next' description='phoneVerifyModal.nextButton' id='cmn9u6' />
        </Button>
      </ModalFooter>
    </React.Fragment>
  );
};

VerificationPrompt.propTypes = {
  goToNextStep: PropTypes.func.isRequired,
  requestChallengeCode: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
  className: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
};
